import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  limit,
  startAfter,
} from "firebase/firestore";
import { db } from "./firebase";
import {
  createMessage,
  validateMessage,
  MESSAGE_TYPE,
  generateChatId,
} from "../types/message";
import { createChatRoom, validateChatRoom } from "../types/chat";

// Collection names
const MESSAGES_COLLECTION = "messages";
const CHAT_ROOMS_COLLECTION = "chatRooms";

/**
 * Service for handling chat operations
 */
export const chatService = {
  /**
   * Send a new message
   * @param {Object} messageData - Message data
   * @param {File} [file] - Optional file to upload
   * @returns {Promise<{success: boolean, messageId: string, error: string}>}
   */
  async sendMessage(messageData, file = null) {
    try {
      // Generate chat ID if not provided
      const chatId =
        messageData.chatId ||
        generateChatId(messageData.senderId, messageData.receiverId);

      let fileUrl = "";
      let fileName = "";
      let messageType = MESSAGE_TYPE.TEXT;

      // Handle file upload if file is provided
      if (file) {
        // You can integrate Cloudinary here for file uploads
        // For now, we'll assume text messages only
        // fileUrl = await this.uploadFile(file);
        // fileName = file.name;
        // messageType = file.type.includes('image') ? MESSAGE_TYPE.IMAGE : MESSAGE_TYPE.FILE;
        throw new Error(
          "File upload not implemented. Use text messages only for now."
        );
      }

      // Create message object
      const message = createMessage({
        ...messageData,
        chatId,
        type: messageType,
        fileUrl,
        fileName,
      });

      // Validate message
      const validation = validateMessage(message);
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(", ")}`);
      }

      // Ensure chat room exists
      await this.getOrCreateChatRoom(message.senderId, message.receiverId, {
        [message.senderId]: {
          name: message.senderName,
          email: message.senderEmail,
        },
        [message.receiverId]: {
          name: message.receiverId, // You might want to fetch this from users collection
          email: message.receiverId, // You might want to fetch this from users collection
        },
      });

      // Add message to Firestore
      const docRef = await addDoc(collection(db, MESSAGES_COLLECTION), {
        ...message,
        timestamp: serverTimestamp(),
        isRead: false,
      });

      // Update chat room's last message and timestamp
      await this.updateChatRoomLastMessage(chatId, {
        text: message.text,
        senderId: message.senderId,
        timestamp: serverTimestamp(),
        type: message.type,
      });

      return {
        success: true,
        messageId: docRef.id,
      };
    } catch (error) {
      console.error("Error sending message:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  },

  /**
   * Get or create a chat room between two users
   * @param {string} userId1 - First user ID
   * @param {string} userId2 - Second user ID
   * @param {Object} participantInfo - Participant information
   * @returns {Promise<Object>} - Chat room data
   */
  async getOrCreateChatRoom(userId1, userId2, participantInfo) {
    try {
      const chatId = generateChatId(userId1, userId2);

      // Check if chat room already exists
      const chatQuery = query(
        collection(db, CHAT_ROOMS_COLLECTION),
        where("participants", "==", [userId1, userId2].sort())
      );

      const querySnapshot = await getDocs(chatQuery);

      if (!querySnapshot.empty) {
        // Return existing chat room
        const doc = querySnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
        };
      }

      // Create new chat room
      const chatRoom = createChatRoom({
        participants: [userId1, userId2],
        participantInfo,
      });

      const validation = validateChatRoom(chatRoom);
      if (!validation.isValid) {
        throw new Error(
          `Chat room validation failed: ${validation.errors.join(", ")}`
        );
      }

      const docRef = await addDoc(collection(db, CHAT_ROOMS_COLLECTION), {
        ...chatRoom,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastMessage: null,
        unreadCount: {
          [userId1]: 0,
          [userId2]: 0,
        },
      });

      return {
        id: docRef.id,
        ...chatRoom,
      };
    } catch (error) {
      console.error("Error getting/creating chat room:", error);
      throw new Error(`Failed to get/create chat room: ${error.message}`);
    }
  },

  /**
   * Get messages for a specific chat
   * @param {string} chatId - Chat room ID
   * @param {number} messageLimit - Number of messages to fetch (default: 50)
   * @returns {Promise<Array>} - List of messages
   */
  async getChatMessages(chatId, messageLimit = 50) {
    try {
      const q = query(
        collection(db, MESSAGES_COLLECTION),
        where("chatId", "==", chatId),
        orderBy("timestamp", "desc"),
        limit(messageLimit)
      );

      const querySnapshot = await getDocs(q);
      const messages = [];

      querySnapshot.forEach((doc) => {
        messages.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      // Return in chronological order
      return messages.reverse();
    } catch (error) {
      console.error("Error getting chat messages:", error);
      throw new Error(`Failed to get messages: ${error.message}`);
    }
  },

  /**
   * Get user's chat rooms
   * @param {string} userId - User ID
   * @returns {Promise<Array>} - List of chat rooms
   */
  async getUserChatRooms(userId) {
    try {
      const q = query(
        collection(db, CHAT_ROOMS_COLLECTION),
        where("participants", "array-contains", userId),
        orderBy("updatedAt", "desc")
      );

      const querySnapshot = await getDocs(q);
      const chatRooms = [];

      querySnapshot.forEach((doc) => {
        chatRooms.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return chatRooms;
    } catch (error) {
      console.error("Error getting user chat rooms:", error);
      throw new Error(`Failed to get chat rooms: ${error.message}`);
    }
  },

  /**
   * Real-time listener for chat messages
   * @param {string} chatId - Chat room ID
   * @param {Function} callback - Callback function when new messages arrive
   * @returns {Function} - Unsubscribe function
   */
  subscribeToChatMessages(chatId, callback) {
    const q = query(
      collection(db, MESSAGES_COLLECTION),
      where("chatId", "==", chatId),
      orderBy("timestamp", "asc")
    );

    return onSnapshot(q, (querySnapshot) => {
      const messages = [];
      querySnapshot.forEach((doc) => {
        messages.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      callback(messages);
    });
  },

  /**
   * Real-time listener for user's chat rooms
   * @param {string} userId - User ID
   * @param {Function} callback - Callback function when chat rooms update
   * @returns {Function} - Unsubscribe function
   */
  subscribeToUserChatRooms(userId, callback) {
    const q = query(
      collection(db, CHAT_ROOMS_COLLECTION),
      where("participants", "array-contains", userId),
      orderBy("updatedAt", "desc")
    );

    return onSnapshot(q, (querySnapshot) => {
      const chatRooms = [];
      querySnapshot.forEach((doc) => {
        chatRooms.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      callback(chatRooms);
    });
  },

  /**
   * Mark messages as read
   * @param {string} chatId - Chat room ID
   * @param {string} userId - User ID marking messages as read
   * @returns {Promise<{success: boolean, error: string}>}
   */
  // Add this method to your existing chatService.js if not present
  async markMessagesAsRead(chatId, userId) {
    try {
      // Get unread messages
      const q = query(
        collection(db, MESSAGES_COLLECTION),
        where("chatId", "==", chatId),
        where("receiverId", "==", userId),
        where("isRead", "==", false)
      );

      const querySnapshot = await getDocs(q);
      const updatePromises = [];

      querySnapshot.forEach((doc) => {
        updatePromises.push(updateDoc(doc.ref, { isRead: true }));
      });

      await Promise.all(updatePromises);

      // Update unread count in chat room
      const chatRoomQuery = query(
        collection(db, CHAT_ROOMS_COLLECTION),
        where("participants", "array-contains", userId)
      );

      const chatSnapshot = await getDocs(chatRoomQuery);
      chatSnapshot.forEach(async (doc) => {
        if (doc.data().participants.includes(userId)) {
          await updateDoc(doc.ref, {
            [`unreadCount.${userId}`]: 0,
            updatedAt: serverTimestamp(),
          });
        }
      });

      return { success: true };
    } catch (error) {
      console.error("Error marking messages as read:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  },
  /**
   * Update chat room's last message
   * @param {string} chatId - Chat room ID
   * @param {Object} lastMessage - Last message data
   */
  async updateChatRoomLastMessage(chatId, lastMessage) {
    try {
      const chatQuery = query(
        collection(db, CHAT_ROOMS_COLLECTION),
        where("participants", "==", chatId.split("_").sort())
      );

      const querySnapshot = await getDocs(chatQuery);

      if (!querySnapshot.empty) {
        const docRef = querySnapshot.docs[0].ref;
        await updateDoc(docRef, {
          lastMessage,
          updatedAt: serverTimestamp(),
          [`unreadCount.${lastMessage.receiverId}`]: serverTimestamp(), // Increment unread count
        });
      }
    } catch (error) {
      console.error("Error updating chat room last message:", error);
    }
  },

  /**
   * Delete a message
   * @param {string} messageId - Message ID
   * @param {string} userId - User ID (for authorization)
   * @returns {Promise<{success: boolean, error: string}>}
   */
  async deleteMessage(messageId, userId) {
    try {
      const messageRef = doc(db, MESSAGES_COLLECTION, messageId);
      const messageDoc = await getDoc(messageRef);

      if (!messageDoc.exists()) {
        throw new Error("Message not found");
      }

      const messageData = messageDoc.data();

      // Check if user is the sender
      if (messageData.senderId !== userId) {
        throw new Error("You can only delete your own messages");
      }

      await deleteDoc(messageRef);
      return { success: true };
    } catch (error) {
      console.error("Error deleting message:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  },
};

export default chatService;
